import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "../css/content-page.scss";
import "../css/about.scss";

const values = [
  {
    number: "01",
    title: "Quality, thoughtfully chosen",
    copy: "We look for vivid aroma, honest flavor and dependable freshness before an ingredient earns a place in the pantry.",
  },
  {
    number: "02",
    title: "People-first service",
    copy: "Useful answers and practical problem-solving matter just as much as what goes inside every sickzuno package.",
  },
  {
    number: "03",
    title: "Curiosity in every jar",
    copy: "We celebrate the cooks, places and traditions that turn a familiar meal into a chance to discover something new.",
  },
];

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const scrollToSection = (sectionId) => {
  document.getElementById(sectionId)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
};

function About() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      scrollToSection(decodeURIComponent(hash.slice(1)));
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [hash]);

  return (
    <main className="content-page about-page">
      <div className="content-page__shell">
        <aside className="content-page__sidebar" aria-label="About page navigation">
          <p className="content-page__sidebar-title">About</p>
          <nav className="content-page__sidebar-nav">
            <section className="content-page__sidebar-group">
              <h2>Explore our story</h2>
              <ul>
                <li>
                  <Link to="/about#who-we-are" onClick={() => scrollToSection("who-we-are")}>
                    Who We Are
                  </Link>
                </li>
                <li>
                  <Link to="/about#values" onClick={() => scrollToSection("values")}>
                    Our Values
                  </Link>
                </li>
                <li>
                  <Link to="/about#our-family" onClick={() => scrollToSection("our-family")}>
                    Our Family
                  </Link>
                </li>
              </ul>
            </section>
          </nav>
        </aside>

        <article
          className="content-page__article content-page__article--wide"
          aria-labelledby="about-title"
        >
          <header className="content-page__header">
            <p className="content-page__eyebrow">Explore sickzuno</p>
            <h1 id="about-title">A wider world of flavor, gathered in one pantry</h1>
            <p className="content-page__lead">
              sickzuno is built for curious cooks who want distinctive spices,
              herbs and culinary ingredients without turning the search into a chore.
            </p>
          </header>

          <figure className="about-page__hero">
            <img
              src="/images/about/a01-spice-workshop.png"
              alt="Two people selecting colorful spices and herbs at a wooden workshop table"
            />
            <figcaption className="about-page__hero-caption">
              <span>Ingredients with a story</span>
              <strong>Selected for the way real kitchens cook.</strong>
            </figcaption>
          </figure>

          <section
            className="content-page__section about-page__story"
            id="who-we-are"
            aria-labelledby="who-we-are-title"
          >
            <p className="content-page__eyebrow">Who We Are</p>
            <h2 id="who-we-are-title">A practical guide to uncommon ingredients</h2>
            <div className="about-page__story-columns">
              <p>
                Our shelves bring together everyday essentials and less familiar finds,
                making it easier to compare flavors, explore new ideas and buy only the
                amount that suits the recipe ahead.
              </p>
              <p>
                We believe a spice shop should feel inviting rather than intimidating.
                Clear descriptions, flexible pantry sizes and responsive customer care
                help every cook move from inspiration to the first fragrant spoonful.
              </p>
            </div>
          </section>

          <section
            className="content-page__section about-page__values"
            id="values"
            aria-labelledby="values-title"
          >
            <p className="content-page__eyebrow">Our Values</p>
            <h2 id="values-title">The principles at our table</h2>
            <p className="about-page__section-intro">
              These three ideas shape how sickzuno presents ingredients and supports
              the people who use them.
            </p>
            <div className="about-page__value-grid">
              {values.map((value) => (
                <article className="about-page__value-card" key={value.title}>
                  <span className="about-page__value-number" aria-hidden="true">
                    {value.number}
                  </span>
                  <h3>{value.title}</h3>
                  <p>{value.copy}</p>
                </article>
              ))}
            </div>
          </section>

          <section
            className="content-page__section about-page__family"
            id="our-family"
            aria-labelledby="family-title"
          >
            <div className="about-page__family-heading">
              <div>
                <p className="content-page__eyebrow">Our Family</p>
                <h2 id="family-title">One pantry, many kinds of cooks</h2>
              </div>
              <p>
                sickzuno connects home cooks, growing kitchens and the people who
                help ingredients make their way from source to shelf.
              </p>
            </div>

            <div className="about-page__family-grid">
              <article>
                <span>For the weeknight table</span>
                <h3>Home cooks</h3>
                <p>
                  Approachable quantities and clear flavor notes make experimentation
                  feel at home in an everyday pantry.
                </p>
              </article>
              <article>
                <span>For ambitious menus</span>
                <h3>Growing kitchens</h3>
                <p>
                  Broader pack choices help bakers, makers and hospitality teams plan
                  recipes at a larger scale.
                </p>
              </article>
              <article>
                <span>Behind every order</span>
                <h3>Our pantry team</h3>
                <p>
                  Product-minded people organize the range and help customers find a
                  useful answer when a choice is not obvious.
                </p>
              </article>
            </div>
          </section>

          <aside className="about-page__cta" aria-label="Explore sickzuno">
            <div>
              <p className="content-page__eyebrow">Let curiosity lead</p>
              <h2>Find the next flavor for your shelf.</h2>
            </div>
            <div className="about-page__cta-links">
              <Link className="about-page__button" to="/shop" onClick={scrollToTop}>
                Shop ingredients
              </Link>
              <Link className="content-page__inline-link" to="/contact" onClick={scrollToTop}>
                Ask our team
              </Link>
            </div>
          </aside>
        </article>
      </div>
    </main>
  );
}

export default About;
